import { useState, useCallback } from 'react';
import { debounce } from 'lodash';
import {
  EnhancedDistrictStats,
  DistrictFilters,
  DistrictSummary,
  EfficiencyRating,
  RiskLevel,
} from '../types/districts.types';
import { DistrictWithStatsDto, MandalType } from '../types/master-data.types';
import { Work, WorkStage, GrantLevel, CategoryPST, WorkType, MandalWorkResponse } from '../types/work.types';
import { masterDataAPI } from '../services/master-data-api.service';
import {
  calculatePerformanceScore,
  getPerformanceRating,
  getRiskLevel,
} from '../utils/districts.utils';
import { worksApiService } from '../services/workApi.service';

import { MandalWithStatsDto } from '../types/master-data.types';
import { EnhancedMandalStats } from '../types/districts.types';
import { Mandal } from '../types/territorial.types';


// Map WorkStage from work.types.ts to master-data.types.ts
const mapWorkStage = (stage: WorkStage): string => {
  switch (stage) {
    case WorkStage.ON_HOLD: return 'HELD_UP';
    case WorkStage.NOT_STARTED: return 'NOT_STARTED';
    case WorkStage.IN_PROGRESS: return 'IN_PROGRESS';
    case WorkStage.COMPLETED: return 'COMPLETED';
    default: return 'NOT_STARTED';
  }
};


interface EnhancedMandal extends Mandal {
    workSummary?: {
      totalWorks: number;
      totalEstimateCost: number;
      totalExpenditure: number;
      financialProgressPercentage: number;
      physicalProgressPercentage: number;
    };
    works?: MandalWorkResponse['works'];
    
    // Add missing properties to match EnhancedMandalStats
    in_progress_works: number;
    delayed_works: number;
    budget_utilization_percentage: number;
    avg_estimate_cost: number;
    physical_progress_percentage?: number;
    completed_works?: number;
    total_works: number;
    total_estimate_cost: number;
    total_expenditure?: number;
    financial_progress_percentage?: number;
  }
  

export const useDistrictsData = () => {
  const [districts, setDistricts] = useState<EnhancedDistrictStats[]>([]);
  const [mandals, setMandals] = useState<EnhancedMandalStats[]>([]); // Changed from EnhancedMandal[]
    const [summary, setSummary] = useState<DistrictSummary>({
    total_districts: 0,
    total_works: 0,
    total_estimate_cost: 0,
    total_expenditure: 0,
    total_length_sanctioned: 0,
    total_length_completed: 0,
    avg_financial_progress: 0,
    avg_physical_progress: 0,
    total_delayed_works: 0,
    total_completed_works: 0,
    highest_performing_district: '',
    lowest_performing_district: '',
    total_mandals: 0,
    avg_works_per_district: 0,
    avg_cost_per_district: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [mandalsLoading, setMandalsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Enhanced district data transformation
  const enhanceDistrictData = useCallback(async (districtsData: DistrictWithStatsDto[]): Promise<EnhancedDistrictStats[]> => {
    return Promise.all(
      districtsData.map(async (district) => {
        const works = await worksApiService.getWorksByDistrict(district.id);
        const total_ras_amount = works.reduce((sum, work) => sum + (work.ras_amount || 0), 0);
        const works_not_started = works.filter(w => w.abstract_stage_of_work === WorkStage.NOT_STARTED).length;
        const works_in_progress = works.filter(w => w.abstract_stage_of_work === WorkStage.IN_PROGRESS).length;
        const works_completed = works.filter(w => w.abstract_stage_of_work === WorkStage.COMPLETED).length;
        const works_on_hold = works.filter(w => w.abstract_stage_of_work === WorkStage.ON_HOLD).length;
        const delayed_works = works.filter(w => worksApiService.isWorkDelayed(w)).length;
        const total_length_sanctioned = works.reduce((sum, work) => sum + (work.length_sanctioned_km || 0), 0);
        const total_length_completed = works.reduce((sum, work) => sum + (work.cumulative_length_completed || 0), 0);
        const total_expenditure = district.total_expenditure || 0;
        const total_estimate_cost = district.total_estimate_cost || 0;
        const budget_utilization_percentage = total_expenditure && total_estimate_cost
          ? (total_expenditure / total_estimate_cost) * 100
          : 0;
        const enhanced: EnhancedDistrictStats = {
          ...district,
          total_ras_amount,
          total_expenditure,
          pending_bills_amount: works.reduce((sum, work) => sum + (work.bills_pending_pao_bro_loc || 0), 0),
          work_not_recorded_amount: works.reduce((sum, work) => sum + (work.work_done_not_recorded_bills || 0), 0),
          budget_utilization_percentage,
          cost_variance: total_ras_amount - total_estimate_cost,
          avg_cost_per_km: total_length_sanctioned > 0 ? total_estimate_cost / total_length_sanctioned : 0,
          total_length_sanctioned,
          total_length_completed,
          physical_progress_percentage: total_length_sanctioned > 0
            ? (total_length_completed / total_length_sanctioned) * 100
            : 0,
          works_not_started,
          works_in_progress,
          works_completed,
          works_on_hold,
          delayed_works,
          on_schedule_works: works_in_progress - delayed_works,
          central_grants: works.filter(w => w.oas_grant_level === GrantLevel.CENTRAL).reduce((sum, w) => sum + (w.ras_amount || w.estimate_cost), 0),
          state_grants: works.filter(w => w.oas_grant_level === GrantLevel.STATE).reduce((sum, w) => sum + (w.ras_amount || w.estimate_cost), 0),
          plain_category_works: works.filter(w => w.category_pst === CategoryPST.PLAIN).length,
          scp_category_works: works.filter(w => w.category_pst === CategoryPST.SCP).length,
          tsp_category_works: works.filter(w => w.category_pst === CategoryPST.TSP).length,
          bt_road_works: works.filter(w => w.work_type === WorkType.NEW_BT_ROAD).length,
          cc_road_works: works.filter(w => w.work_type === WorkType.MAINTENANCE).length,
          bridge_works: works.filter(w => w.work_type === WorkType.REHABILITATION).length,
          building_works: works.filter(w => w.work_type === WorkType.UPGRADATION).length,
          mandals_count: district.mandal_count || 0,
          last_updated: district.updatedAt || new Date().toISOString(),
          performance_score: 0,
          efficiency_rating: EfficiencyRating.Average,
          risk_level: RiskLevel.Medium,
        };
        enhanced.performance_score = calculatePerformanceScore(enhanced);
        enhanced.efficiency_rating = getPerformanceRating(
          enhanced.financial_progress_percentage || 0,
          enhanced.physical_progress_percentage || 0
        ).rating;
        enhanced.risk_level = getRiskLevel(
          enhanced.delayed_works,
          enhanced.total_works,
          enhanced.budget_utilization_percentage
        );
        return enhanced;
      })
    );
  }, []);

  // Apply filters to districts data
  const applyFilters = useCallback((data: EnhancedDistrictStats[], filters: DistrictFilters): EnhancedDistrictStats[] => {
    let filteredData = [...data];
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = filteredData.filter(
        d => d.name.toLowerCase().includes(searchLower) || d.code.toLowerCase().includes(searchLower)
      );
    }
    if (filters.minWorks !== undefined) filteredData = filteredData.filter(d => d.total_works >= filters.minWorks!);
    if (filters.maxWorks !== undefined) filteredData = filteredData.filter(d => d.total_works <= filters.maxWorks!);
    if (filters.minCost !== undefined) filteredData = filteredData.filter(d => d.total_estimate_cost >= filters.minCost! * 100000);
    if (filters.maxCost !== undefined) filteredData = filteredData.filter(d => d.total_estimate_cost <= filters.maxCost! * 100000);
    if (filters.minProgress !== undefined) filteredData = filteredData.filter(d => (d.financial_progress_percentage || 0) >= filters.minProgress!);
    if (filters.maxProgress !== undefined) filteredData = filteredData.filter(d => (d.financial_progress_percentage || 0) <= filters.maxProgress!);
    if (filters.performanceRating) filteredData = filteredData.filter(d => d.efficiency_rating === filters.performanceRating);
    if (filters.riskLevel) filteredData = filteredData.filter(d => d.risk_level === filters.riskLevel);
    return filteredData;
  }, []);

  // Apply sorting to districts data
  const applySorting = useCallback((data: EnhancedDistrictStats[], filters: DistrictFilters): EnhancedDistrictStats[] => {
    return [...data].sort((a, b) => {
      const aValue = a[filters.sortBy as keyof EnhancedDistrictStats] as any;
      const bValue = b[filters.sortBy as keyof EnhancedDistrictStats] as any;
      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      }
      return filters.sortOrder === 'ASC' ? comparison : -comparison;
    });
  }, []);

  // Calculate summary statistics
  const calculateSummary = useCallback((filteredData: EnhancedDistrictStats[]): DistrictSummary => {
    const totalWorks = filteredData.reduce((sum, d) => sum + d.total_works, 0);
    const totalCost = filteredData.reduce((sum, d) => sum + d.total_estimate_cost, 0);
    const totalExpenditure = filteredData.reduce((sum, d) => sum + (d.total_expenditure || 0), 0);
    const totalLength = filteredData.reduce((sum, d) => sum + (d.total_length_sanctioned || 0), 0);
    const totalCompleted = filteredData.reduce((sum, d) => sum + (d.total_length_completed || 0), 0);
    const totalDelayed = filteredData.reduce((sum, d) => sum + (d.delayed_works || 0), 0);
    const totalCompletedWorks = filteredData.reduce((sum, d) => sum + (d.works_completed || 0), 0);
    const totalMandals = filteredData.reduce((sum, d) => sum + (d.mandals_count || 0), 0);
    const avgFinancial = filteredData.length > 0
      ? filteredData.reduce((sum, d) => sum + (d.financial_progress_percentage || 0), 0) / filteredData.length
      : 0;
    const avgPhysical = filteredData.length > 0
      ? filteredData.reduce((sum, d) => sum + (d.physical_progress_percentage || 0), 0) / filteredData.length
      : 0;
    const sortedByPerformance = [...filteredData].sort((a, b) => b.performance_score - a.performance_score);
    return {
      total_districts: filteredData.length,
      total_works: totalWorks,
      total_estimate_cost: totalCost,
      total_expenditure: totalExpenditure,
      total_length_sanctioned: totalLength,
      total_length_completed: totalCompleted,
      avg_financial_progress: avgFinancial,
      avg_physical_progress: avgPhysical,
      total_delayed_works: totalDelayed,
      total_completed_works: totalCompletedWorks,
      highest_performing_district: sortedByPerformance[0]?.name || '',
      lowest_performing_district: sortedByPerformance[sortedByPerformance.length - 1]?.name || '',
      total_mandals: totalMandals,
      avg_works_per_district: filteredData.length > 0 ? totalWorks / filteredData.length : 0,
      avg_cost_per_district: filteredData.length > 0 ? totalCost / filteredData.length : 0,
    };
  }, []);

  // Fetch districts data
  const fetchDistricts = useCallback(
    debounce(async (filters: DistrictFilters) => {
      setLoading(true);
      setError(null);
      try {
        const districtsData = await masterDataAPI.getCached('districts-stats', () =>
          masterDataAPI.getDistrictsWithStats()
        );
        const enhancedData = await enhanceDistrictData(districtsData);
        let processedData = applyFilters(enhancedData, filters);
        processedData = applySorting(processedData, filters);
        setDistricts(processedData);
        setSummary(calculateSummary(processedData));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch districts data');
      } finally {
        setLoading(false);
      }
    }, 300),
    [enhanceDistrictData, applyFilters, applySorting, calculateSummary]
  );


  const enhanceMandals = useCallback(async (mandalsData: MandalWithStatsDto[]): Promise<EnhancedMandalStats[]> => {
    return Promise.all(
      mandalsData.map(async (mandal) => {
        const mandalId = (mandal as any).mandal_id || mandal.id;
        if (!mandalId) return { ...mandal, mandal_type: mandal.mandal_type || 'RURAL' } as any;
        const mandalWorksResponse = await worksApiService.getWorksByMandal(mandalId); // Returns { works: Work[], ... }
        const works = mandalWorksResponse.works; // Extract the works array
        const total_works = works.length;
        const total_estimate_cost = works.reduce((sum, work) => sum + (work.estimate_cost || 0), 0);
        const total_expenditure = works.reduce((sum, work) => sum + (work.cum_exp_on_work || 0), 0);
        const in_progress_works = works.filter(w => w.abstract_stage_of_work === WorkStage.IN_PROGRESS).length;
        const delayed_works = works.filter(w => worksApiService.isWorkDelayed(w)).length;
        const completed_works = works.filter(w => w.abstract_stage_of_work === WorkStage.COMPLETED).length;
        const budget_utilization_percentage = total_expenditure && total_estimate_cost
          ? (total_expenditure / total_estimate_cost) * 100
          : 0;
        const avg_estimate_cost = total_works > 0 ? total_estimate_cost / total_works : 0;
        const total_length_sanctioned = works.reduce((sum, work) => sum + (work.length_sanctioned_km || 0), 0);
        const total_length_completed = works.reduce((sum, work) => sum + (work.cumulative_length_completed || 0), 0);
        const physical_progress_percentage = total_length_sanctioned > 0
          ? (total_length_completed / total_length_sanctioned) * 100
          : 0;
  
        const enhanced: EnhancedMandalStats = {
          ...mandal,
          mandal_type: MandalType.RURAL,
          in_progress_works,
          delayed_works,
          budget_utilization_percentage,
          avg_estimate_cost,
          physical_progress_percentage,
          completed_works,
          total_works,
          total_estimate_cost,
          total_expenditure,
          financial_progress_percentage: mandal.financial_progress_percentage || 0,
        };
        return enhanced;
      })
    );
  }, []);

  // Fetch mandals for selected district
  const fetchMandals = useCallback(async (districtId: number) => {
    setMandalsLoading(true);
    setError(null);
    try {
      const mandalsData = await masterDataAPI.getCached(`mandals-${districtId}`, () =>
        masterDataAPI.getMandalsWithStats(districtId)
      );
      const enhancedMandals = await enhanceMandals(mandalsData);
      // Type assertion as temporary fix
      setMandals(enhancedMandals as EnhancedMandalStats[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch mandals data');
    } finally {
      setMandalsLoading(false);
    }
  }, []);

  // Retry failed requests
  const retry = useCallback(async (filters: DistrictFilters, districtId?: number) => {
    if (districtId) {
      await fetchMandals(districtId);
    } else {
      await fetchDistricts(filters);
    }
  }, [fetchDistricts, fetchMandals]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reset mandals data
  const resetMandals = useCallback(() => {
    setMandals([]);
  }, []);

  return {
    districts,
    mandals,
    summary,
    loading,
    mandalsLoading,
    error,
    fetchDistricts,
    fetchMandals,
    retry,
    clearError,
    resetMandals,
  };
};